/**
 * @file Categories.jsx
 * @description Page de gestion des catégories de l'application
 * 
 * Cette page gère l'affichage et la manipulation des catégories avec les fonctionnalités suivantes :
 * 
 * 1. Gestion des données
 *    - Chargement initial automatique via useEffect
 *    - Rechargement automatique après les opérations CRUD
 *    - Gestion des états de chargement et d'erreur
 *    - Pagination et filtrage côté client
 * 
 * 2. Configuration du tableau
 *    - Colonnes personnalisables avec tri
 *    - Filtres primaires et secondaires
 *    - Actions sur les lignes et en-tête
 *    - Support multilingue
 * 
 * 3. Gestion des états
 *    - État de chargement (loading)
 *    - Gestion des erreurs
 *    - Pagination (page courante, items par page)
 *    - Filtres et recherche
 *    - Configuration du tri
 * 
 * 4. Fonctionnalités de rafraîchissement
 *    - Rechargement manuel via refreshData
 *    - Rechargement automatique après CRUD
 *    - Réinitialisation de la pagination lors des changements
 * 
 * 5. Internationalisation
 *    - Support multilingue (FR, ES, AR)
 *    - Traductions des libellés
 *    - Format des nombres et dates
 * 
 * @example
 * // Utilisation du composant
 * <Categories />
 * 
 * @example
 * // Configuration du tableau
 * const tableConfig = {
 *   pagination: { ... },
 *   sorting: { ... }
 * };
 * 
 * @example
 * // Configuration des colonnes
 * const columns = [
 *   {
 *     key: 'name',
 *     label: 'Nom',
 *     sortable: true,
 *     visible: true
 *   }
 * ];
 * 
 * @example
 * // Configuration des filtres
 * const filterConfig = {
 *   primary: [
 *     {
 *       type: 'select',
 *       key: 'category',
 *       label: 'Catégorie',
 *       options: [...]
 *     }
 *   ]
 * };
 * 
 * @example
 * // Gestion du rechargement des données
 * const refreshData = useCallback(() => {
 *   setRefreshTrigger(prev => prev + 1);
 * }, []);
 * 
 * @example
 * // Chargement automatique des données
 * useEffect(() => {
 *   fetchData({
 *     page: currentPage,
 *     limit: itemsPerPage,
 *     search,
 *     filters,
 *     sortConfig
 *   });
 * }, [fetchData, currentPage, itemsPerPage, search, filters, sortConfig, refreshTrigger]);
 */

import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useLanguage } from '../../../../../hooks/useLanguage';
import { categories as frCategories } from '../../../../../locales/fr/categories';
import { categories as esCategories } from '../../../../../locales/es/categories';
import { categories as arCategories } from '../../../../../locales/ar/categories';
import { CategoryService } from '../../../../../services/AppDataServices/services/category.service';
import { ProductService } from '../../../../../services/AppDataServices/services/product.service';
import DataTable from '../../../components/DataTable';
import FilterSection from '../../../components/DataTable/FilterSection';
import useSearch from '../../../hooks/useSearch';
import PrintableView from '../../../components/DataTable/PrintableView';
import Modal from '../../../components/common/Modal';
import { aside as frAside } from '../../../../../locales/fr/aside';
import { aside as arAside } from '../../../../../locales/ar/aside';
import { aside as esAside } from '../../../../../locales/es/aside';
import useMyAppStore from '../../../../../stores/myAppStore';
import CategoryForm from './CategoryForm';
import { formatDate } from '../../../../../utils';

const translations = {
  fr: frCategories,
  es: esCategories,
  ar: arCategories
};

const asideTranslations = {
  fr: frAside,
  ar: arAside,
  es: esAside
};

const Categories = () => {
  const { lang } = useLanguage();
  const t = translations[lang];
  const asideT = asideTranslations[lang];
  const { setPageTitle } = useMyAppStore();
  
  // Create services in the correct order
  const productService = new ProductService();
  const categoryService = new CategoryService(productService);
  
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // État pour la pagination et les filtres
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({
    key: 'name',
    direction: 'asc'
  });

  // Configuration de la recherche
  const searchConfig = useMemo(() => ({
    minMatchLength: 2,
    fields: [
      { key: 'name', type: 'text' },
      { key: 'description', type: 'text' }
    ]
  }), []);

  const { searchItems } = useSearch(searchConfig);

  // Handlers pour les filtres
  const handleSearchChange = useCallback((value) => {
    setSearch(value);
    setCurrentPage(1);
  }, []);

  // Configuration des filtres primaires
  const primaryFilters = useMemo(() => [
    {
      type: 'search',
      key: 'search',
      label: t.filters.search.label,
      placeholder: t.filters.search.placeholder,
      value: search,
      onChange: handleSearchChange
    },
    {
      type: 'sort',
      key: 'sort',
      label: t.filters.sort?.label || 'Trier par',
      value: sortConfig.key,
      direction: sortConfig.direction,
      onChange: (value) => {
        setSortConfig(prev => ({
          key: value,
          direction: prev.key === value && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
      },
      options: [
        { value: 'name', label: t.columns.name },
        { value: 'description', label: t.columns.description }
      ]
    }
  ], [t, search, handleSearchChange, sortConfig]);

  // Configuration des colonnes
  const columns = useMemo(() => [
    {
      key: 'name',
      label: t.columns.name,
      sortable: true,
      visible: true
    },
    {
      key: 'description',
      label: t.columns.description,
      sortable: true,
      visible: true
    },
    {
      key: 'image',
      label: t.columns.image,
      sortable: false,
      visible: true,
      render: (item) => (
        item.image ? (
          <img 
            src={item.image} 
            alt={item.name} 
            className="w-10 h-10 object-cover rounded"
          />
        ) : (
          <span className="text-gray-400">-</span>
        )
      )
    }
  ], [t]);

  // Configuration des colonnes collapsibles
  const collapsibleColumns = useMemo(() => [
    {
      key: 'basicInfo',
      label: t.columns.basicInfo || 'Informations de base',
      render: (item) => (
        <dl className="mt-2 space-y-2">
          <div className="flex items-center gap-2">
            <dt className="text-gray-500 min-w-[100px]">{t.columns.name}:</dt>
            <dd className="flex-1">{item.name || 'Non disponible'}</dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="text-gray-500 min-w-[100px]">{t.columns.description}:</dt>
            <dd className="flex-1">{item.description || 'Non disponible'}</dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="text-gray-500 min-w-[100px]">{t.columns.image}:</dt>
            <dd className="flex-1">
              {item.image ? (
                <img 
                  src={item.image} 
                  alt={item.name} 
                  className="w-20 h-20 object-cover rounded-lg"
                />
              ) : (
                <span className="text-gray-400">Aucune image</span>
              )}
            </dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="text-gray-500 min-w-[100px]">{t.columns.createdAt}:</dt>
            <dd className="flex-1">{formatDate(item.createdAt)}</dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="text-gray-500 min-w-[100px]">{t.columns.updatedAt}:</dt>
            <dd className="flex-1">{formatDate(item.updatedAt)}</dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="text-gray-500 min-w-[100px]">{t.columns.lastSync}:</dt>
            <dd className="flex-1">{item.lastSync ? formatDate(item.lastSync) : 'Non synchronisé'}</dd>
          </div>
        </dl>
      )
    }
  ], [t]);

  // Fonction de récupération des données
  const fetchData = useCallback(async ({ page, limit, search, filters, sortConfig }) => {
    try {
      setLoading(true);
      setError(null);
      
      let categories = await categoryService.getAll();
      let total = categories.length;

      // Application de la recherche avancée
      if (search) {
        categories = searchItems(search, categories);
      }

      // Appliquer le tri
      if (sortConfig.key) {
        categories.sort((a, b) => {
          const aValue = a[sortConfig.key];
          const bValue = b[sortConfig.key];
          const direction = sortConfig.direction === 'asc' ? 1 : -1;
          
          if (typeof aValue === 'string') {
            return direction * aValue.localeCompare(bValue);
          }
          return direction * (aValue - bValue);
        });
      }

      // Mettre à jour le total après tous les filtres
      total = categories.length;

      // Pagination
      const start = (page - 1) * limit;
      const end = start + limit;
      categories = categories.slice(start, end);

      setData({ items: categories, total });
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [searchItems]);

  // Effet pour charger les données
  useEffect(() => {
    fetchData({
      page: currentPage,
      limit: itemsPerPage,
      search,
      filters,
      sortConfig
    });
  }, [fetchData, currentPage, itemsPerPage, search, filters, sortConfig, refreshTrigger]);

  // Handlers pour les changements d'état
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1);
  }, []);

  const handleSortChange = useCallback((newSortConfig) => {
    setSortConfig(newSortConfig);
  }, []);

  // Fonction pour forcer le rechargement des données
  const refreshData = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Configuration des actions
  const actionConfig = useMemo(() => ({
    primary: [
      {
        label: t.actions.newCategory,
        onClick: () => {
          setSelectedCategory(null);
          setIsEditMode(false);
          setIsModalOpen(true);
        },
        primary: true
      }
    ],
    row: [
      {
        icon: 'edit',
        onClick: (item) => {
          setSelectedCategory(item);
          setIsEditMode(true);
          setIsModalOpen(true);
        },
        title: t.actions.edit,
        className: 'text-blue-500 hover:text-blue-700'
      },
      {
        icon: 'delete',
        onClick: async (item) => {
          try {
            if (window.confirm(t.confirmations.delete)) {
              await categoryService.deleteCategory(item.id);
              refreshData();
            }
          } catch (error) {
            alert(error.message.includes('contient des produits')
              ? t.errors?.cannotDeleteWithProducts || 'Impossible de supprimer la catégorie car elle contient des produits'
              : t.errors?.deleteError || 'Erreur lors de la suppression de la catégorie'
            );
          }
        },
        title: t.actions.delete,
        className: 'text-red-500 hover:text-red-700'
      }
    ]
  }), [t, refreshData]);

  // Configuration complète pour le DataTable
  const dataTableConfig = useMemo(() => ({
    tableConfig: {
      pagination: {
        pageSize: 10,
        pageSizeOptions: [10, 25, 50, 100]
      },
      sorting: {
        defaultSort: { key: 'name', direction: 'asc' }
      }
    },
    columns,
    filters: {
      primary: primaryFilters
    },
    actions: actionConfig,
    data,
    loading,
    error,
    currentPage,
    itemsPerPage,
    totalItems: data.total,
    onPageChange: handlePageChange,
    onItemsPerPageChange: handleItemsPerPageChange,
    onSearch: handleSearchChange,
    onSortChange: handleSortChange,
    onFilterChange: (newFilters) => {
      setFilters(prev => ({ ...prev, ...newFilters }));
      setCurrentPage(1);
    },
    refreshData,
    collapsibleColumns
  }), [
    columns,
    primaryFilters,
    actionConfig,
    data,
    loading,
    error,
    currentPage,
    itemsPerPage,
    handlePageChange,
    handleItemsPerPageChange,
    handleSearchChange,
    handleSortChange,
    refreshData,
    collapsibleColumns
  ]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handlers pour les opérations CRUD
  const handleSubmitCategory = async (formData) => {
    try {
      setIsSubmitting(true);
      if (isEditMode) {
        await categoryService.updateCategory(selectedCategory.id, formData);
      } else {
        // Créer la catégorie et récupérer la catégorie créée
        const createdCategory = await categoryService.createCategory(formData);
        
        // Passer en mode édition avec la catégorie créée
        setSelectedCategory(createdCategory);
        setIsEditMode(true); // Passer en mode édition
      }
      refreshData();
    } catch (error) {
      setError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fonction pour remettre en mode création
  const handleResetToCreate = () => {
    setSelectedCategory(null);
    setIsEditMode(false); // Retour en mode création
  };

  useEffect(() => {
    setPageTitle(asideT.routes.categories || 'Catégories');
  }, [lang, asideT.routes.categories, setPageTitle]);

  return (
    <>
      <div className="space-y-4 print-hide">
        <FilterSection
          primaryFilters={primaryFilters}
          onSearch={handleSearchChange}
          onFilterChange={(newFilters) => {
            setFilters(prev => ({ ...prev, ...newFilters }));
            setCurrentPage(1);
          }}
          onSortChange={handleSortChange}
          onAdd={() => {
            setSelectedCategory(null);
            setIsEditMode(false);
            setIsModalOpen(true);
          }}
        />
        <DataTable {...dataTableConfig} />
      </div>
      <PrintableView
        title={t.title || 'Catégories'}
        columns={columns}
        data={data.items}
        translations={t}
      />
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={isEditMode ? t.form.editTitle : t.form.createTitle}
        width="max-w-xl"
      >
        <CategoryForm
          category={selectedCategory}
          isEditMode={isEditMode}
          categories={data.items}
          onSubmit={handleSubmitCategory}
          onCancel={() => setIsModalOpen(false)}
          isSubmitting={isSubmitting}
          onResetToCreate={handleResetToCreate}
        />
      </Modal>
    </>
  );
};

export default Categories; 